import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';
import { useAuth } from '../../../contexts/AuthContext';
import { usePrivacy } from '../../../contexts/PrivacyContext';

const CollaborationNetworkGraph = ({ activitiesData }) => {
    const svgRef = useRef(null);
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [networkData, setNetworkData] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showLegend, setShowLegend] = useState(false);
    const { groupMembers } = useAuth();
    const { maskName } = usePrivacy();

    // Handle window resizing
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                setDimensions({ width, height });
            }
        };

        handleResize(); // Initialize dimensions
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Generate network data from activities
    useEffect(() => {
        if (!activitiesData || activitiesData.length === 0) return;

        // Create member color mapping
        const memberColorMap = {};
        if (groupMembers && groupMembers.length > 0) {
            groupMembers.forEach(member => {
                if (member.email && member.color) {
                    memberColorMap[member.email] = member.color;
                }
            });
        }

        // Find all unique members
        const uniqueMembers = new Set();
        activitiesData.forEach(activity => {
            if (activity.contributors && Array.isArray(activity.contributors)) {
                activity.contributors.forEach(contributor => {
                    if (contributor.email) {
                        uniqueMembers.add(contributor.email);
                    }
                });
            }
        });

        // Count solo activities for each member
        const soloActivityCounts = {};
        activitiesData.forEach(activity => {
            if (activity.contributors && activity.contributors.length === 1 && activity.contributors[0].email) {
                const email = activity.contributors[0].email;
                soloActivityCounts[email] = (soloActivityCounts[email] || 0) + 1;
            }
        });

        // Create nodes array with fixed initial positions
        const nodeCount = uniqueMembers.size;
        const nodes = Array.from(uniqueMembers).map((email, index) => {
            // Calculate fixed positions in a circle layout
            const angle = (index / nodeCount) * 2 * Math.PI;
            const radius = Math.min(dimensions.width, dimensions.height) * 0.35; // Use 35% of the smallest dimension
            const x = dimensions.width / 2 + radius * Math.cos(angle);
            const y = dimensions.height / 2 + radius * Math.sin(angle);
            
            // Find first appearance of this member to get name and other properties
            const firstAppearance = activitiesData.find(activity => 
                activity.contributors && 
                activity.contributors.some(c => c.email === email)
            );
            
            const contributor = firstAppearance?.contributors.find(c => c.email === email);
            const color = memberColorMap[email] || contributor?.color || '#cccccc'; // Use member's defined color
            
            const maskedNodeName = maskName(contributor?.name || email.split('@')[0]);
            const maskedNodeInitial = (() => {
                const parts = maskedNodeName.trim().split(/\s+/);
                return parts.length >= 2
                    ? (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
                    : parts[0].charAt(0).toUpperCase();
            })();
            return {
                id: email,
                name: maskedNodeName,
                initial: maskedNodeInitial,
                color: color, // Add color property
                soloActivities: soloActivityCounts[email] || 0, // Count of solo activities
                participationCount: activitiesData.filter(activity => 
                    activity.contributors && 
                    activity.contributors.some(c => c.email === email)
                ).length,
                x: x, // Initial x position
                y: y, // Initial y position
                fx: x, // Fixed x position
                fy: y  // Fixed y position
            };
        });

        // Create links array (representing collaboration relationships)
        const links = [];
        const collaborationCounts = {};

        // For each activity with multiple participants
        activitiesData.forEach(activity => {
            if (!activity.contributors || activity.contributors.length < 2) return;
            
            // For each activity, all participants have collaboration relationships
            for (let i = 0; i < activity.contributors.length; i++) {
                for (let j = i + 1; j < activity.contributors.length; j++) {
                    const source = activity.contributors[i].email;
                    const target = activity.contributors[j].email;
                    
                    if (!source || !target) continue;
                    
                    // Create unique identifier to count collaborations between pairs
                    const linkId = [source, target].sort().join('--');
                    collaborationCounts[linkId] = (collaborationCounts[linkId] || 0) + 1;
                }
            }
        });

        // Get all collaboration values, including solo activities
        const collaborationValues = [...Object.values(collaborationCounts)];
        // Add solo activity counts to the values array for consistent scaling
        Object.values(soloActivityCounts).forEach(count => {
            if (count > 0) collaborationValues.push(count);
        });
        
        // Calculate max and min for all types of activities
        const maxCollaboration = Math.max(...collaborationValues, 1); // Ensure non-zero max
        const minCollaboration = Math.min(...collaborationValues, 1); // Ensure non-zero min

        // Convert collaboration counts to links
        Object.entries(collaborationCounts).forEach(([linkId, count]) => {
            const [source, target] = linkId.split('--');
            
            // Find source and target node colors for gradient creation
            const sourceNode = nodes.find(n => n.id === source);
            const targetNode = nodes.find(n => n.id === target);
            
            links.push({
                source,
                target,
                value: count, // Collaboration count as edge weight
                sourceColor: sourceNode?.color || '#cccccc',
                targetColor: targetNode?.color || '#cccccc',
                // Add normalized value for line thickness and color depth
                normalizedValue: maxCollaboration === minCollaboration ? 
                    1 : (count - minCollaboration) / (maxCollaboration - minCollaboration)
            });
        });

        // Add self-loop links for solo activities 
        // Using the same normalization formula as shared activities
        nodes.forEach(node => {
            if (node.soloActivities > 0) {
                links.push({
                    source: node.id,
                    target: node.id,
                    value: node.soloActivities,
                    isSelfLoop: true,
                    color: node.color,
                    // Use the same normalization formula for consistent thickness
                    normalizedValue: maxCollaboration === minCollaboration ? 
                        1 : (node.soloActivities - minCollaboration) / (maxCollaboration - minCollaboration)
                });
            }
        });

        setNetworkData({ nodes, links, maxCollaboration, minCollaboration });
    }, [activitiesData, groupMembers, dimensions, maskName]);

    // Draw network graph
    useEffect(() => {
        if (!networkData || !svgRef.current || dimensions.width === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove(); // Clear existing content

        const width = dimensions.width;
        const height = dimensions.height;
        
        // Create force-directed graph with less movement
        const simulation = d3.forceSimulation(networkData.nodes)
            .force("link", d3.forceLink(networkData.links.filter(l => !l.isSelfLoop)) // Filter out self-loops for force layout
                .id(d => d.id)
                .distance(150)) // Increase distance between nodes
            .force("charge", d3.forceManyBody().strength(-100)) // Reduce repulsion strength
            .force("center", d3.forceCenter(width / 2, height / 2))
            .force("collision", d3.forceCollide().radius(d => 20 + Math.sqrt(d.participationCount) * 2 + 15)); // Increase collision radius

        // Drastically reduce the number of simulation iterations to minimize movement
        simulation.alphaDecay(0.1); // Faster decay of the simulation's "energy"

        // Create gradient definitions for connection lines
        const defs = svg.append("defs");
        
        // Create unique gradient ID for each edge
        networkData.links.filter(l => !l.isSelfLoop).forEach((link, i) => {
            const gradId = `grad-${i}`;
            const grad = defs.append("linearGradient")
                .attr("id", gradId)
                .attr("gradientUnits", "userSpaceOnUse");
                
            // Dynamically set gradient start and end points (will be updated in tick function)
            const sourceColor = d3.color(link.sourceColor);
            const targetColor = d3.color(link.targetColor);
            
            grad.append("stop")
                .attr("offset", "0%")
                .attr("stop-color", sourceColor ? sourceColor.darker(0.5).toString() : link.sourceColor);
                
            grad.append("stop")
                .attr("offset", "100%")
                .attr("stop-color", targetColor ? targetColor.darker(0.5).toString() : link.targetColor);
                
            link.gradId = gradId; // Store gradient ID in link data
        });

        // Container for zooming and panning
        const container = svg.append("g");

        // Line thickness scale to enhance visual difference
        const linkWidthScale = d3.scaleLinear()
            .domain([0, 1]) // Use normalized values
            .range([2, 12]); // Increase thickness range from [1.5, 8] to [2, 12] for more contrast

        // Create regular links (not self-loops)
        const link = container.append("g")
            .selectAll("line")
            .data(networkData.links.filter(l => !l.isSelfLoop))
            .enter().append("line")
            .attr("stroke-width", d => {
                // Add minimum thickness for low collaboration to ensure visibility
                return Math.max(2, linkWidthScale(d.normalizedValue)); 
            })
            .attr("stroke", d => {
                // Use gradient with deeper colors
                const sourceColor = d3.color(d.sourceColor);
                const targetColor = d3.color(d.targetColor);
                if (sourceColor) sourceColor.darker(0.5);
                if (targetColor) targetColor.darker(0.5);
                
                // Still use original gradient ID
                return `url(#${d.gradId})`;
            })
            .attr("stroke-opacity", 0.9) // Increase opacity for deeper colors
            .attr("stroke-linecap", "round"); // Add round line caps for smoother appearance

        // Create node groups
        const node = container.append("g")
            .selectAll(".node")
            .data(networkData.nodes)
            .enter().append("g")
            .attr("class", "node")
            .call(d3.drag()
                .on("start", dragstarted)
                .on("drag", dragged)
                .on("end", dragended));

        // Function to create self-loop arc
        const createSelfLoopPath = (d, radius) => {
            const selfLoopRadius = 12; // Slightly larger for better visibility
            const x = d.x || 0;
            const y = d.y || 0;
            const nodeRadius = 20 + Math.sqrt(d.participationCount) * 2;
            
            // Create a small arc loop above the node
            return `M ${x - selfLoopRadius} ${y - nodeRadius - 5} 
                   A ${selfLoopRadius} ${selfLoopRadius} 0 1 1 ${x + selfLoopRadius} ${y - nodeRadius - 5}`;
        };
        
        // Create self-loop arcs for solo activities - will be updated in tick function
        const selfLoops = container.append("g")
            .selectAll(".self-loop")
            .data(networkData.links.filter(l => l.isSelfLoop))
            .enter().append("path")
            .attr("class", "self-loop")
            .attr("fill", "none")
            .attr("stroke", d => {
                // Make self-loop color deeper
                const color = d3.color(d.color);
                return color ? color.darker(0.5).toString() : d.color;
            })
            .attr("stroke-width", d => Math.max(2, linkWidthScale(d.normalizedValue)))
            .attr("stroke-opacity", 0.9) // Increase opacity
            .attr("stroke-linecap", "round");

        // Add circle for each node - with size indicating participation
        node.append("circle")
            .attr("r", d => 20 + Math.sqrt(d.participationCount) * 2)
            .attr("fill", d => {
                // Make the color significantly darker
                const color = d3.color(d.color);
                if (color) {
                    color.opacity = 1.0; // Full opacity
                    return color.darker(0.7).toString(); // Make the color much darker
                }
                return d.color;
            })
            .attr("stroke", "#fff") // Add white border
            .attr("stroke-width", 2) // Border thickness
            .style("cursor", "pointer")
            .on("click", (event, d) => {
                // Toggle fixed position when node is clicked
                if (d.fx && d.fy) {
                    d.fx = d.fy = null; // Unfix position
                } else {
                    d.fx = d.x; // Fix at current position
                    d.fy = d.y;
                }
                setSelectedNode(selectedNode === d.id ? null : d.id);
            });

        // Add solo activity indicators as small stars/badges on nodes with solo activities
        node.filter(d => d.soloActivities > 0)
            .append("g")
            .attr("transform", d => {
                const nodeRadius = 20 + Math.sqrt(d.participationCount) * 2;
                return `translate(${nodeRadius * 0.5}, ${-nodeRadius * 0.5})`;
            })
            .append("text")
            .attr("text-anchor", "middle")
            .attr("dy", ".3em")
            .attr("fill", "#fff")
            .attr("stroke", "#000")
            .attr("stroke-width", 0.5)
            .style("font-weight", "bold")
            .style("font-size", "10px")
            .text(d => d.soloActivities);

        // Add initial label for each node
        node.append("text")
            .attr("text-anchor", "middle")
            .attr("dy", ".3em")
            .attr("fill", d => {
                // Determine appropriate text color based on background color brightness
                const color = d3.color(d.color);
                const luminance = color ? 0.2126 * color.r / 255 + 0.7152 * color.g / 255 + 0.0722 * color.b / 255 : 0.5;
                return luminance > 0.5 ? "#000" : "#fff";
            })
            .style("font-weight", "bold")
            .style("pointer-events", "none")
            .text(d => d.initial);

        // Add name label for each node
        node.append("text")
            .attr("dy", 30)
            .attr("text-anchor", "middle")
            .style("font-size", "12px")
            .text(d => d.name);

        // Update layout
        simulation.on("tick", () => {
            // Update connection line positions
            link
                .attr("x1", d => d.source.x)
                .attr("y1", d => d.source.y)
                .attr("x2", d => d.target.x)
                .attr("y2", d => d.target.y);
            
            // Update self-loop positions and paths
            selfLoops
                .attr("d", d => {
                    const sourceNode = networkData.nodes.find(node => node.id === d.source);
                    if (sourceNode) {
                        return createSelfLoopPath(sourceNode);
                    }
                    return "";
                });
                
            // Update gradient definition coordinates
            networkData.links.filter(l => !l.isSelfLoop).forEach((link, i) => {
                if (link.source.x && link.source.y && link.target.x && link.target.y) {
                    defs.select(`#grad-${i}`)
                        .attr("x1", link.source.x)
                        .attr("y1", link.source.y)
                        .attr("x2", link.target.x)
                        .attr("y2", link.target.y);
                }
            });

            // Update node positions
            node.attr("transform", d => {
                // Keep nodes within view boundaries
                d.x = Math.max(30, Math.min(width - 30, d.x));
                d.y = Math.max(30, Math.min(height - 30, d.y));
                return `translate(${d.x}, ${d.y})`;
            });
        });

        // Zoom functionality
        const zoom = d3.zoom()
            .scaleExtent([0.5, 5])
            .on("zoom", (event) => {
                container.attr("transform", event.transform);
            });

        svg.call(zoom);

        // Drag functionality
        function dragstarted(event) {
            if (!event.active) simulation.alphaTarget(0.1).restart(); // Reduced energy
            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
        }

        function dragged(event) {
            event.subject.fx = event.x;
            event.subject.fy = event.y;
        }

        function dragended(event) {
            if (!event.active) simulation.alphaTarget(0);
            // Keep position fixed after drag
            // Don't reset fx and fy to null
        }

        // Highlight selected node and its connections
        if (selectedNode) {
            // Reduce opacity of all unrelated nodes
            node.selectAll("circle")
                .attr("opacity", d => {
                    const isConnected = d.id === selectedNode || 
                        networkData.links.some(link => 
                            (link.source.id === selectedNode && link.target.id === d.id) || 
                            (link.target.id === selectedNode && link.source.id === d.id)
                        );
                    return isConnected ? 1 : 0.2; // Reduce opacity of unconnected nodes
                });

            // Reduce opacity of all unrelated links
            link.attr("opacity", d => 
                (d.source.id === selectedNode || d.target.id === selectedNode) ? 1 : 0.05 // Enhanced contrast
            );
            
            // Highlight self-loops for selected node
            selfLoops.attr("opacity", d => 
                d.source === selectedNode ? 1 : 0.05
            );
        } else {
            // Reset opacity
            node.selectAll("circle").attr("opacity", 1);
            link.attr("opacity", 0.7);
            selfLoops.attr("opacity", 0.7);
        }

        
    }, [networkData, dimensions, selectedNode]);

    return (
        <GraphContainer ref={containerRef}>
            <TitleContainer>
                <Title>
                    Collaboration Network
                    <InfoIconContainer 
                        onMouseEnter={() => setShowLegend(true)}
                        onMouseLeave={() => setShowLegend(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showLegend && (
                            <LegendTooltip>
                                <LegendTitle>Network Legend</LegendTitle>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            backgroundColor: '#4285f4', 
                                            width: '22px', 
                                            height: '22px', 
                                            borderRadius: '50%',
                                            border: '2px solid #fff'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Node size = 20 + √(total activities) × 2</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)', 
                                            width: '30px', 
                                            height: '6px',
                                            borderRadius: '2px'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Line thickness = collaboration frequency (2-12px)</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            background: 'linear-gradient(90deg, #667eea, #764ba2)', 
                                            width: '30px', 
                                            height: '4px',
                                            borderRadius: '2px'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Line colors blend from member colors</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol style={{position: 'relative'}}>
                                        <div style={{
                                            backgroundColor: '#ff6b6b', 
                                            width: '20px', 
                                            height: '20px', 
                                            borderRadius: '50%',
                                            border: '2px solid #fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: '10px',
                                            fontWeight: 'bold'
                                        }}>M</div>
                                        <span style={{
                                            position: 'absolute', 
                                            top: '-2px', 
                                            right: '-2px', 
                                            fontSize: '8px', 
                                            backgroundColor: '#fff', 
                                            borderRadius: '50%', 
                                            width: '12px', 
                                            height: '12px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            border: '1px solid #000',
                                            fontWeight: 'bold',
                                            color: '#000'
                                        }}>3</span>
                                    </LegendSymbol>
                                    <LegendText>Number badge shows solo activities count</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '16px', 
                                            height: '16px', 
                                            borderRadius: '50%',
                                            border: '2px solid #333',
                                            backgroundColor: '#e0e0e0',
                                            position: 'relative'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: '-10px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '24px',
                                                height: '4px',
                                                borderRadius: '12px',
                                                backgroundColor: '#333'
                                            }}></div>
                                        </div>
                                    </LegendSymbol>
                                    <LegendText>Self-loop arc above node = solo activities</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '16px', 
                                            height: '16px', 
                                            borderRadius: '50%',
                                            backgroundColor: '#4285f4',
                                            border: '2px solid #fff',
                                            color: '#fff',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer'
                                        }}>M</div>
                                    </LegendSymbol>
                                    <LegendText>Click nodes to highlight connections</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '20px', 
                                            height: '20px', 
                                            borderRadius: '50%',
                                            background: 'conic-gradient(#ff6b6b, #4ecdc4, #ffd93d, #6bcf7f, #ff6b6b)',
                                            border: '2px solid #fff'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Colors match assigned member colors</LegendText>
                                </LegendItem>
                            </LegendTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <SVG ref={svgRef} 
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            preserveAspectRatio="xMidYMid meet" 
            />
        </GraphContainer>
    );
};

const GraphContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
`;

const Title = styled.h3`
    font-size: 16px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const LegendTooltip = styled.div`
    position: absolute;
    top: 25px;
    left: -140px;
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 15px;
    width: 280px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const LegendTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const LegendSymbol = styled.div`
    width: 20px;
    height: 20px;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LegendText = styled.div`
    font-size: 12px;
    color: #555;
    line-height: 1.3;
`;

const SVG = styled.svg`
    display: block;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    flex: 1;
`;

export default React.memo(CollaborationNetworkGraph); 